import { FaceDissatisfied } from "@carbon/icons-react";
import { Loading } from "@carbon/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addModal, closeModal, toggleModal, selectModals } from "../../features/application/applicationSlice.js";
import { profileUpdateThunk, avatarUploadThunk } from "../../features/user/userThunks.js";
import { getModalById } from "../../utils/modalUtils.js";
import {
    selectProfileUser,
    selectProfileAvatar,
    selectUserUsername,
    selectUserFirstName,
    selectUserLastName,
    selectUserEmail,
    selectProfileLoading,
    selectUpdateLoading,
    selectAvatarLoading,
    selectUserError
} from "../../features/user/userSlice.js";
import ProfileHeader from "./ProfileHeader/index.js";
import ProfileInfo from "./ProfileInfo/index.js";
import EditProfileModal from "./EditProfileModal/index.js";
import EditAvatarModal from "./EditAvatarModal/index.js";
import { useTranslation } from "react-i18next";

// Constants outside component
const MODAL_PROFILE = 'profile';
const MODAL_AVATAR = 'iconEdit';
const INITIAL_FORM_DATA = {
    username: '',
    first_name: '',
    last_name: ''
};

const Index = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const profileUser = useSelector(selectProfileUser);
    const profileAvatar = useSelector(selectProfileAvatar);
    const username = useSelector(selectUserUsername);
    const firstName = useSelector(selectUserFirstName);
    const lastName = useSelector(selectUserLastName);
    const email = useSelector(selectUserEmail);
    const profileLoading = useSelector(selectProfileLoading);
    const updateLoading = useSelector(selectUpdateLoading);
    const avatarLoading = useSelector(selectAvatarLoading);
    const error = useSelector(selectUserError);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA)
    const [fileState, setFileState] = useState({ filename: '', file: '' })

    const modal = useSelector(selectModals)
    const profileModalState = getModalById(modal, MODAL_PROFILE)?.state ?? false
    const avatarModalState = getModalById(modal, MODAL_AVATAR)?.state ?? false

    useEffect(() => {
        dispatch(addModal(MODAL_PROFILE))
        dispatch(addModal(MODAL_AVATAR))
    }, [dispatch])

    const handleEditProfile = () => dispatch(toggleModal(MODAL_PROFILE));
    const handleEditAvatar = () => dispatch(toggleModal(MODAL_AVATAR));
    const handleCloseProfile = () => dispatch(closeModal(MODAL_PROFILE));
    const handleCloseAvatar = () => dispatch(closeModal(MODAL_AVATAR));

    const handleFileChange = (returnValue) => {
        setFileState((state) => ({
            ...state,
            filename: returnValue.filename,
            file: returnValue.file
        }));
    };

    const handleAvatarUpload = () => {
        if (fileState.file) {
            dispatch(avatarUploadThunk(fileState.file))
            handleCloseAvatar()
            setFileState({ filename: '', file: '' })
        }
    };

    function updateProfile() {
        if (!profileUser) {
            return
        }
        
        const completePayload = {
            ...profileUser,
            user: {
                ...profileUser,
                ...formData
            }
        }
        dispatch(profileUpdateThunk(completePayload))
        handleCloseProfile()
    }

    return (
        <div className="page-container">
            <div className="profile-container">
                {profileLoading && (
                    <div className="status-container">
                        <Loading description={t("profile.loading")} withOverlay={false} />
                    </div>
                )}
                {error && (
                    <div className="status-container">
                        <h2>{t("profile.loadError")}</h2>
                        <FaceDissatisfied size={32} />
                    </div>
                )}
                {profileUser && (
                    <>
                        <ProfileHeader 
                            username={username}
                            profileAvatar={profileAvatar}
                            onEditAvatar={handleEditAvatar}
                        />
                        <ProfileInfo
                            firstName={firstName}
                            lastName={lastName}
                            email={email}
                            onEditProfile={handleEditProfile}
                        />
                        <EditProfileModal
                            isOpen={profileModalState}
                            onClose={handleCloseProfile}
                            formData={formData}
                            onChange={setFormData}
                            onSave={updateProfile}
                            updateLoading={updateLoading}
                            profileUser={profileUser}
                        />
                        <EditAvatarModal
                            isOpen={avatarModalState}
                            onClose={handleCloseAvatar}
                            onSave={handleAvatarUpload}
                            updateLoading={avatarLoading}
                            onFileChange={handleFileChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default Index;
